import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
// Slider images
import slide1 from './assets/hero1.jpg';
import slide2 from './assets/hero2.jpg';
import slide3 from './assets/hero3.jpg';
import slide4 from './assets/hero4.jpg';
import motivationImg from './assets/motivation.jpg';
import ILandingPage, {features, heroSlides, navItems, partners, pricing} from "./data";

const slideVariants: Variants = {
    initial: dir => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    animate: { x: 0, opacity: 1, transition: { duration: 0.8 } },
    exit: dir => ({ x: dir < 0 ? 300 : -300, opacity: 0, transition: { duration: 0.5 } }),
};
const textVariants: Variants = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const LandingPage: React.FC<ILandingPage> = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    // hide scrollbar and scroll-snapping
    useEffect(() => {
        // Disable scroll-snapping on mobile devices
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            document.body.style.overflow = '';
            return;
        }
        document.body.style.overflow = 'hidden';
        let isScroll = false;
        const sections = navItems.map(i=>i.id);
        const onWheel = (e: WheelEvent) =>{
            if(isScroll) return;
            const dir = e.deltaY;
            const pos = window.scrollY;
            const tops = sections.map(id => document.getElementById(id)?.offsetTop||0);
            let idx = tops.findIndex((t,i)=> pos < (tops[i+1]||Infinity));
            let next=idx;
            if(dir>0) next=Math.min(sections.length-1,idx+1);
            else if(dir<0) next=Math.max(0,idx-1);
            if(next!==idx){ isScroll=true; document.getElementById(sections[next])?.scrollIntoView({behavior:'smooth'}); setTimeout(()=>isScroll=false,800);}    };
        window.addEventListener('wheel', onWheel, {passive:true});
        return ()=>{window.removeEventListener('wheel',onWheel); document.body.style.overflow='';};
    },[]);

    const [[page,dir], setPage] = useState<[number,number]>([0,0]);
    const idx = ((page%heroSlides.length)+heroSlides.length)%heroSlides.length;
    const paginate = d=>setPage([page+d,d]);

    return (
        <div className="scroll-smooth font-sans">
            {/* Header */}
            <header className="fixed top-0 w-full bg-white backdrop-blur-lg shadow-md z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
                    <button onClick={()=>document.getElementById('hero')?.scrollIntoView({behavior:'smooth'})} className="text-xl sm:text-2xl font-bold text-[#0080ff] focus:outline-none">Optik App</button>
                    <div className="sm:hidden">
                        <button onClick={()=>setMenuOpen(!menuOpen)} className="p-2 focus:outline-none">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={menuOpen?'M6 18L18 6M6 6l12 12':'M4 6h16M4 12h16M4 18h16'} />
                            </svg>
                        </button>
                    </div>
                    <nav className="relative">

                        <ul className={`
  ${menuOpen ? 'block' : 'hidden'}
  sm:block
  absolute top-16 left-0 w-full bg-white shadow-md p-4 space-y-4
  sm:static sm:shadow-none sm:p-0 sm:flex sm:space-y-0 sm:space-x-8
`}>
                            {navItems.map(item => (
                                <li key={item.id}>
                                    <button
                                        onClick={() => { document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); }}
                                        className="block text-gray-700 hover:text-[#0080ff] transition-colors focus:outline-none"
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>
            </header>

            <main className="pt-16">
                {/* Hero */}
                <section id="hero" className="relative h-screen overflow-hidden">
                    <AnimatePresence initial={false} custom={dir}>
                        <motion.div key={page} custom={dir} variants={slideVariants} initial="initial" animate="animate" exit="exit"
                                    className="absolute inset-0"
                                    style={{background:heroSlides[idx].bg}}>
                            <div className="absolute inset-0 bg-black/60 z-0" />
                            <motion.div key={page} variants={textVariants} initial="initial" animate="animate"
                                        className="relative z-10 flex flex-col justify-center items-start h-full px-4 sm:px-6 lg:px-24 text-white">
                                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold mb-4 drop-shadow-lg max-w-2xl">
                                    {heroSlides[idx].title}
                                </h1>
                                <p className="text-base sm:text-lg lg:text-xl drop-shadow-md max-w-xl">
                                    {heroSlides[idx].desc}
                                </p>
                            </motion.div>
                        </motion.div>
                    </AnimatePresence>
                    <div className="absolute inset-0 flex justify-between items-center px-4 sm:px-6 lg:px-24 z-20">
                        <button onClick={()=>paginate(-1)} className="bg-white/30 hover:bg-white/50 text-white rounded-full p-2 sm:p-3">
                            ‹
                        </button>
                        <button onClick={()=>paginate(1)} className="bg-white/30 hover:bg-white/50 text-white rounded-full p-2 sm:p-3">
                            ›
                        </button>
                    </div>
                </section>

                {/* Features */}
                <section id="features" className="py-16 sm:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.h2 variants={textVariants} initial="initial" whileInView="animate"
                                   className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            Öne Çıkan Özellikler
                        </motion.h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {features.map((f,i)=>(
                                <motion.div key={i} variants={textVariants} initial="initial" whileInView="animate"
                                            className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
                                    <div className="text-3xl mb-4 text-[#0080ff]">{f.icon}</div>
                                    <h3 className="text-xl sm:text-2xl font-semibold mb-2 text-gray-800">{f.title}</h3>
                                    <p className="text-gray-600 text-sm sm:text-base">{f.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Partners */}
                <section id="partners" className="py-16 sm:py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.h2 variants={textVariants} initial="initial" whileInView="animate"
                                   className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            İş Ortaklarımız
                        </motion.h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {partners.map((p,i)=>(
                                <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                                    <motion.div variants={textVariants} initial="initial" whileInView="animate"
                                                className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition text-center">
                                        <img src={p.logo} alt={p.name} className="h-12 sm:h-16 mx-auto mb-2 object-contain" />
                                        <p className="text-gray-700 font-medium text-sm sm:text-base">{p.name}</p>
                                    </motion.div>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Pricing */}
                <section id="pricing" className="py-16 sm:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.h2 variants={textVariants} initial="initial" whileInView="animate"
                                   className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12">
                            Fiyatlandırma
                        </motion.h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {pricing.map((plan,i)=>(
                                <motion.div key={i} variants={textVariants} initial="initial" whileInView="animate"
                                            className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
                                    <h3 className="text-xl sm:text-2xl font-semibold mb-1 text-gray-900">{plan.tier}</h3>
                                    <p className="text-gray-500 mb-2 text-sm">Starting at</p>
                                    <p className="text-4xl sm:text-5xl font-bold text-gray-900">{plan.price}<span className="text-base font-normal">/month</span></p>
                                    <p className="text-gray-600 mt-2 mb-4 text-sm sm:text-base">{plan.description}</p>
                                    <hr className="border-dashed border-gray-300 my-4" />
                                    <ul className="space-y-2 mb-4 text-sm sm:text-base">
                                        {plan.features.map((feat,j)=>(<li key={j} className="flex items-start text-gray-700"><span className="mr-2 text-green-500">✓</span><span>{feat}</span></li>))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section id="contact" className="py-16 sm:py-20 bg-gray-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-12">
                        <div className="w-full lg:w-1/2">
                            <motion.h2 variants={textVariants} initial="initial" whileInView="animate" className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">Bizimle İletişime Geçin</motion.h2>
                            <form className="space-y-4 sm:space-y-6 bg-white p-6 sm:p-8 rounded-lg shadow-md">
                                <div><label className="block text-gray-700 mb-1">Adınız</label><input type="text" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0080ff] text-sm sm:text-base" /></div>
                                <div><label className="block text-gray-700 mb-1">E-posta</label><input type="email" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0080ff] text-sm sm:text-base" /></div>
                                <div><label className="block text-gray-700 mb-1">Mesajınız</label><textarea rows={4} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0080ff] text-sm sm:text-base" /></div>
                                <button type="submit" className="w-full px-4 py-2 sm:px-6 sm:py-3 bg-[#0080ff] text-white rounded-full font-semibold hover:bg-[#005bb5] transition text-sm sm:text-base">Gönder</button>
                            </form>
                        </div>
                        <div className="w-full lg:w-1/2 flex flex-col justify-center items-start space-y-4 sm:space-y-6">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-800">Başarıya Ulaşın</h3>
                            <p className="text-gray-600 text-sm sm:text-base">CO-HR ile hedeflerinizi netleştirin, performansı izleyin ve verimliliği artırın.</p>
                            <img src={motivationImg} alt="Motivational" className="w-full rounded-lg shadow-md object-cover h-48 sm:h-64 lg:h-80" />
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-gray-900 text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm sm:text-base">
                        <div><h5 className="font-bold mb-3">Ürünler</h5><ul className="space-y-2 text-gray-300"><li><button className="hover:text-white focus:outline-none">Özellikler</button></li><li><button className="hover:text-white focus:outline-none">API</button></li><li><button className="hover:text-white focus:outline-none">Entegrasyonlar</button></li></ul></div>
                        <div><h5 className="font-bold mb-3">Kaynaklar</h5><ul className="space-y-2 text-gray-300"><li><button className="hover:text-white focus:outline-none">Blog</button></li><li><button className="hover:text-white focus:outline-none">Dokümantasyon</button></li><li><button className="hover:text-white focus:outline-none">API Referans</button></li></ul></div>
                        <div><h5 className="font-bold mb-3">Şirket</h5><ul className="space-y-2 text-gray-300"><li><button className="hover:text-white focus:outline-none">Hakkımızda</button></li><li><button className="hover:text-white focus:outline-none">Kariyer</button></li><li><button className="hover:text-white focus:outline-none">İletişim</button></li></ul></div>
                        <div><h5 className="font-bold mb-3">Destek</h5><ul className="space-y-2 text-gray-300"><li><button className="hover:text-white focus:outline-none">Destek Merkezi</button></li><li><button className="hover:text-white focus:outline-none">SSS</button></li><li><button className="hover:text-white focus:outline-none">Güvenlik</button></li></ul></div>
                    </div>
                    <div className="mt-8 text-center text-gray-500 text-xs sm:text-sm">© 2025 Optik V2 App. Tüm hakları saklıdır.</div>
                </footer>
            </main>
        </div>
    );
};

export default LandingPage;
